"use client";
import CartNotification from "@/components/molecules/CartNotification/CartNotification";
import { GeneratedCartItem } from "@/lib/centra/localAtomics";
import { getProductUrl } from "@/lib/centra/productUri";
import useRemoveFromCart from "@/lib/hooks/removeFromCart";
import { CartItemMaybeHooked, CartItemWithHooks } from "@/lib/hooks/useItems";
import { formatPrice } from "@/lib/utils/price";
import DeleteSvg from "@/public/icons/24px/Delete.svg";
import Link from "next/link";
import React, { useRef } from "react";
import CartCounter from "../CartCounter";
import CartImage from "./CartImage/CartImage";
import styles from "./cart-item.module.css";

type CartItemProps = {
  item: CartItemMaybeHooked;
} & {
  groupItemsIds?: string[];
};

const CartItem = ({ item, groupItemsIds }: CartItemProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null!);
  const isCartItemWithHooks = (
    item: CartItemMaybeHooked | GeneratedCartItem
  ): item is CartItemWithHooks => {
    return (item as CartItemWithHooks).increment !== undefined;
  };
  const isWithHooks = isCartItemWithHooks(item);
  const { removeItem } = useRemoveFromCart();
  const isRecipe = item.type === "recipe";
  const isOnSale = Boolean(item.priceAsNumber) && Boolean(item.priceBeforeDiscount) ? item.priceAsNumber! < item.priceBeforeDiscount! : false;

  const removeAllGroupItems = () => {
    if (groupItemsIds) {
      groupItemsIds.forEach((id) => {
        removeItem.mutate({
          line: id
        });
      });
    }
  };

  /**
   * Custom logic here to handle click on the cart item for everything
   * except the buttons and other links iniside
   */
  const handleCartItemClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (
      target.closest("a") ||
      target.closest("button") ||
      target.closest("." + styles.counter) ||
      target.tagName === "BUTTON"
    )
      return;

    linkRef.current?.click();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles["cart-item"]} onClick={handleCartItemClick}>
        <CartImage image={item?.firstThumbnail || ""} alt="" type={item.type} />

        <div className={styles.details}>
          <div className={styles["first-row"]}>
            <div className={styles["name-container"]}>
              <Link
                ref={linkRef}
                href={getProductUrl({ uri: item.uri, type: item.type })}
                className={styles.name}
              >
                {item.name}
              </Link>

              <p className={styles["variant"]}>{item.variant}</p>
            </div>

            {isWithHooks && (
              <button onClick={isRecipe ? removeAllGroupItems : item.remove}>
                <DeleteSvg />
              </button>
            )}
          </div>

          <div className={styles["last-row"]}>
          <p>{formatPrice(item?.priceAsNumber)} {isOnSale && <span className={styles.discountedPrice}>{formatPrice(item.priceBeforeDiscount)}</span>}</p>

            {isWithHooks ? (
              <div className={styles.counter}>
                <CartCounter quantity={item.quantity} line={item.line} />
              </div>
            ) : (
              <p className={styles.variant}>{item.quantity} stk</p>
            )}
          </div>
        </div>
      </div>

      {((isRecipe && item.requiredYarns > 0) ||
        (item.type === "booklet" && item.requiredYarns > 0)) && (
        <CartNotification
          type="info"
          message={`Når du kjøper denne oppskriften, må du også kjøpe minst ${item.requiredYarns ?? 4} valgfrie
            garnnøster.`}
        />
      )}
    </div>
  );
};

export default CartItem;
